/**
 * PRPreviewCard Component
 * 
 * Displays a pull request preview with stats and publish button.
 * Part of the json-render component system.
 */

import { CardContent, Button } from '@pagespeedify/ui';
import {
    GitBranch,
    FileCode,
    Clock,
    CheckCircle2,
    ExternalLink,
    AlertTriangle,
    Loader2,
} from 'lucide-react';

import type { PRPreviewCardProps } from '../../types/json-render';

interface PRPreviewCardComponentProps extends PRPreviewCardProps {
    prUrl?: string;
    onPublish?: () => void;
    isPublishing?: boolean;
}

export default function PRPreviewCard({
    branch,
    baseBranch = 'main',
    title,
    description,
    stats,
    timeElapsed,
    prUrl,
    showPublishButton = false,
    onPublish,
    isPublishing = false,
    finalizationStatus,
    finalizationError,
    verificationSummary,
}: PRPreviewCardComponentProps) {
    const normalizedFinalizationStatus = String(finalizationStatus || '').toLowerCase();
    const finalizationQueued = normalizedFinalizationStatus === 'queued';
    const finalizationRunning = normalizedFinalizationStatus === 'running';
    const finalizationFailed = normalizedFinalizationStatus === 'failed';
    const finalizationSkipped = normalizedFinalizationStatus === 'skipped';
    const finalizationActive = finalizationQueued || finalizationRunning;
    const publishDisabled = isPublishing || finalizationActive;
    const header = (() => {
        if (finalizationActive) {
            return {
                icon: <Loader2 className="h-5 w-5 animate-spin text-amber-500" />,
                text: finalizationQueued ? 'PR creation queued...' : 'Creating PR...'
            };
        }
        if (finalizationFailed) {
            return {
                icon: <AlertTriangle className="h-5 w-5 text-amber-500" />,
                text: 'PR creation failed'
            };
        }
        if (finalizationSkipped) {
            return {
                icon: <AlertTriangle className="h-5 w-5 text-amber-500" />,
                text: 'PR creation skipped'
            };
        }
        if (prUrl) {
            return {
                icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
                text: 'Pull request published'
            };
        }
        return {
            icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
            text: 'Ready for review (PR not published yet)'
        };
    })();
    const cardToneClass = finalizationFailed
        ? 'bg-amber-500/10'
        : (finalizationActive || finalizationSkipped)
            ? 'bg-amber-500/5'
            : 'bg-green-500/5';
    return (
        <div className={`${cardToneClass} overflow-hidden`}>
            <CardContent className="p-4">


                {/* Header */}
                <div className="flex items-center gap-2 mb-4">
                    {header.icon}
                    <h3 className="font-semibold text-lg">{header.text}</h3>
                </div>
                {finalizationFailed && finalizationError && (
                    <p className="text-sm text-muted-foreground mb-3">
                        {finalizationError}
                    </p>
                )}

                {/* Branch info */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                    <GitBranch className="h-4 w-4" />
                    <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{branch}</code>
                    <span>{'->'}</span>
                    <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{baseBranch}</code>
                </div>

                {/* Title */}
                <h4 className="font-medium mb-1">{title}</h4>

                {/* Description */}
                {description && (
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                        {description}
                    </p>
                )}



                {/* Footer */}
                <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        {stats.filesChanged && (
                            <span className="flex items-center gap-1">
                                <FileCode className="h-3 w-3" />
                                {stats.filesChanged} files
                            </span>
                        )}
                        {timeElapsed && (
                            <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {timeElapsed}
                            </span>
                        )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2">
                        {prUrl && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(prUrl, '_blank')}
                                className="gap-2"
                            >
                                <ExternalLink className="h-3.5 w-3.5" />
                                View on GitHub
                            </Button>
                        )}


                        {showPublishButton && onPublish && (
                            <Button
                                size="sm"
                                onClick={onPublish}
                                disabled={publishDisabled}
                                className="bg-green-600 hover:bg-green-700"
                            >
                                {publishDisabled ? 'Publishing...' : 'Publish PR'}
                            </Button>
                        )}
                    </div>
                </div>


            </CardContent>
        </div>
    );
}

